import Vue from 'vue';
import { componentFactoryOf } from 'vue-tsx-support';

import { injectStylesMixin } from '../../mixins/inject-styles';

import styles from './jtn-ui-range-slider.styles.scss?module';

interface Events {
  onSliderChanged: void;
  onMoveStart: Vue;
  onMoveEnd: Vue;
  onInput: string;
  onCallback: string;
}

type DotStyles = {
  width: string;
  height: string;
  top: string;
};

type ProcessSign = {
  pos: number[];
  start: number;
};

type ComponentData = {
  processSign: ProcessSign | null;
  flag: boolean;
  keydownFlag: boolean | null;
  focusFlag: boolean;
  processFlag: boolean;
  currentValue: number[];
  currentSlider: number;
  size: number;
  focusSlider: number;
  isComponentExists: boolean;
  height: number;
  offset: number;
};

export default componentFactoryOf<Events>()
  .mixin(injectStylesMixin(styles))
  .create({
    name: 'JtnUiRangeSlider',
    props: {
      caption: {
        type: String,
        default: ''
      },
      value: {
        type: Array as unknown as () => [number, number],
        required: true
      },
      min: {
        type: Number,
        default: 0
      },
      max: {
        type: Number,
        default: 100
      },
      buttonsSize: {
        type: Number,
        default: 14
      },
      interval: {
        type: Number,
        default: 1
      },
      disabled: {
        type: Boolean,
        default: false
      },
      speed: {
        type: Number,
        default: 0.2
      },
      debug: {
        type: Boolean,
        default: true
      },
      useKeyboard: {
        type: Boolean,
        default: false
      }
    },
    data(): ComponentData {
      return {
        processSign: null,
        flag: false,
        keydownFlag: null,
        focusFlag: false,
        processFlag: false,
        currentValue: [],
        currentSlider: 0,
        size: 0,
        focusSlider: 0,
        isComponentExists: true,
        height: 2,
        offset: 0
      };
    },
    computed: {
      isDisabled(): boolean {
        return this.disabled;
      },
      slider(): HTMLElement[] {
        return [
          this.$refs.sliderBtnLeft as HTMLElement,
          this.$refs.sliderBtnRight as HTMLElement
        ];
      },
      val: {
        get(): number[] {
          return this.currentValue;
        },
        set(val: number[]) {
          this.currentValue = val;
        }
      },
      currentIndex(): number[] {
        return [
          (this.currentValue[0] - this.min) / this.interval,
          (this.currentValue[1] - this.min) / this.interval
        ];
      },
      multiple(): number {
        const [, decimals] = `${this.interval}`.split('.');
        return decimals ? 10 ** decimals.length : 1;
      },
      total(): number {
        const diff = Math.floor((this.max - this.min) * this.multiple);

        if (diff % (this.interval * this.multiple) !== 0) {
          this.printError(
            'Prop[interval] is illegal, Please make sure that the interval can be divisible'
          );
        }
        return (this.max - this.min) / this.interval;
      },
      gap(): number {
        return this.size / this.total;
      },
      position(): number[] {
        return [
          ((this.currentValue[0] - this.min) / this.interval) * this.gap,
          ((this.currentValue[1] - this.min) / this.interval) * this.gap
        ];
      },
      limit(): number[][] {
        return [
          [0, this.position[1]],
          [this.position[0], this.size]
        ];
      },
      valueLimit(): number[][] {
        return [
          [this.min, this.currentValue[1]],
          [this.currentValue[0], this.max]
        ];
      },
      idleSlider(): 0 | 1 {
        return this.currentSlider === 0 ? 1 : 0;
      },
      dotStyles(): DotStyles {
        return {
          width: `${this.buttonsSize}px`,
          height: `${this.buttonsSize}px`,
          top: `${-(this.buttonsSize - this.height) / 2}px`
        };
      }
    },
    watch: {
      value(val) {
        this.flag || this.setValue(val, true);
      },
      max(val) {
        if (val < this.min) {
          this.printError('The maximum value can not be less than the minimum value.');
          return;
        }
        const resetVal = this.limitValue(this.val);
        this.setValue(resetVal);
        this.refresh();
      },
      min(val) {
        if (val > this.max) {
          this.printError('The minimum value can not be greater than the maximum value.');
          return;
        }

        const resetVal = this.limitValue(this.val);
        this.setValue(resetVal);
        this.refresh();
      }
    },
    mounted() {
      this.isComponentExists = true;

      if (typeof window === 'undefined' || typeof document === 'undefined') {
        this.printError('window or document is undefined, can not be initialization.');
        return;
      }

      this.$nextTick(() => {
        if (this.isComponentExists) {
          const resetVal = this.limitValue(this.value as number[]);
          this.getStaticData();
          this.setValue(resetVal, true, 0);

          document.addEventListener(
            'touchmove',
            (event: TouchEvent) => this.moving(event, event.targetTouches[0].clientX),
            { passive: false }
          );
          document.addEventListener('touchend', () => this.moveEnd(), { passive: false });
          document.addEventListener('mousedown', event => this.blurSlider(event));
          document.addEventListener('mousemove', (event: MouseEvent) =>
            this.moving(event, event.clientX)
          );
          document.addEventListener('mouseup', () => this.moveEnd());
          document.addEventListener('mouseleave', () => this.moveEnd());
          window.addEventListener('resize', () => this.refresh());
        }
      });
    },
    beforeDestroy() {
      this.isComponentExists = false;

      document.removeEventListener('touchmove', (event: TouchEvent) =>
        this.moving(event, event.targetTouches[0].clientX)
      );
      document.removeEventListener('touchend', () => this.moveEnd());
      document.removeEventListener('mousedown', event => this.blurSlider(event));
      document.removeEventListener('mousemove', (event: MouseEvent) =>
        this.moving(event, event.clientX)
      );
      document.removeEventListener('mouseup', () => this.moveEnd());
      document.removeEventListener('mouseleave', () => this.moveEnd());
      window.removeEventListener('resize', () => this.refresh());
    },
    methods: {
      wrapClick(event: MouseEvent) {
        if (this.isDisabled || this.processFlag) {
          return;
        }

        const pos = event.clientX - this.offset;
        const diffPosition = this.position[1] - this.position[0];
        this.currentSlider = pos > diffPosition / 2 + this.position[0] ? 1 : 0;

        this.$emit('sliderChanged');
        this.setValueOnPos(pos);
      },
      moveSlideByTouch(event: TouchEvent, index = 0, isProcess = false) {
        this.moveStart(event.targetTouches[0].clientX, index, isProcess);
      },
      moveSlideByClick(event: MouseEvent, index = 0, isProcess = false) {
        this.moveStart(event.clientX, index, isProcess);
      },
      moveStart(coordinate: number, index = 0, isProcess = false) {
        if (this.isDisabled) {
          return;
        }

        this.currentSlider = index;

        if (isProcess) {
          this.processFlag = true;
          this.processSign = {
            pos: this.position,
            start: this.getPos(coordinate)
          };
        }

        if (!isProcess && this.useKeyboard) {
          this.focusFlag = true;
          this.focusSlider = index;
        }

        this.flag = true;
        this.$emit('moveStart', this);
      },
      moving(event: TouchEvent | MouseEvent, coordinate: number) {
        if (!this.flag) {
          return;
        }

        event.preventDefault();

        if (this.processFlag && this.processSign !== null) {
          this.currentSlider = 0;
          this.setValueOnPos(
            this.processSign.pos[0] + this.getPos(coordinate) - this.processSign.start,
            true
          );

          this.currentSlider = 1;
          this.setValueOnPos(
            this.processSign.pos[1] + this.getPos(coordinate) - this.processSign.start,
            true
          );
        } else {
          this.setValueOnPos(this.getPos(coordinate), true);
        }
      },
      moveEnd() {
        if (this.flag) {
          this.$emit('moveEnd', this);
        } else {
          return;
        }

        this.flag = false;
        window.setTimeout(() => {
          this.processFlag = false;
        }, 0);

        this.setPosition();
      },
      getStaticData() {
        if (this.$refs.sliderElement) {
          this.size = (this.$refs.sliderElement as HTMLElement).offsetWidth;
          this.offset = (
            this.$refs.sliderElement as HTMLElement
          ).getBoundingClientRect().left;
        }
      },
      blurSlider(event: Event) {
        const nameButton = this.focusSlider === 1 ? 'Right' : 'Left';
        const dot = this.$refs[`sliderBtn${nameButton}`];
        if (!dot || dot === event.target) {
          return;
        }

        this.focusFlag = false;
      },
      getPos(coordinate: number) {
        return coordinate - this.offset;
      },
      setValueOnPos(pos: number, isDrag = false) {
        const range = this.limit[this.currentSlider];
        const valueRange = this.valueLimit[this.currentSlider];

        if (pos >= range[0] && pos <= range[1]) {
          const currentValue =
            (Math.round(pos / this.gap) * (this.interval * this.multiple) +
              this.min * this.multiple) /
            this.multiple;

          this.setTransform(pos);
          this.setCurrentValue(currentValue, isDrag);
        } else if (pos < range[0]) {
          this.setTransform(range[0]);
          this.setCurrentValue(valueRange[0]);

          if (this.currentSlider === 1) {
            this.focusSlider = 0;
            this.currentSlider = 0;
          }
        } else {
          this.setTransform(range[1]);
          this.setCurrentValue(valueRange[1]);

          if (this.currentSlider === 0) {
            this.focusSlider = 1;
            this.currentSlider = 1;
          }
        }
      },
      isDiff(a: number | number[], b: number | number[]) {
        if (Array.isArray(a) && Array.isArray(b) && a.length === b.length) {
          return a.some((v, i) => v !== b[i]);
        }
        return a !== b;
      },
      setCurrentValue(val: number, bool = false, isIdleSlider = false) {
        const slider = isIdleSlider ? this.idleSlider : this.currentSlider;

        if (val < this.min || val > this.max) {
          return;
        }

        if (this.isDiff(this.currentValue[slider], val)) {
          this.currentValue.splice(slider, 1, val);
          this.syncValue();
        }

        bool || this.setPosition();
      },
      setIndex(val: number | number[]) {
        if (Array.isArray(val)) {
          const value = [
            this.interval * val[0] + this.min,
            this.interval * val[1] + this.min
          ];

          this.setValue(value);
        } else {
          const newVal = this.interval * val + this.min;
          const diff: number = this.currentValue[1] - this.currentValue[0];
          this.currentSlider = newVal > diff / 2 + this.currentValue[0] ? 1 : 0;
          this.setCurrentValue(val);
        }
      },
      setValue(val: number[], noCb = false, speed = 0) {
        if (this.isDiff(this.val, val)) {
          const resetVal = this.limitValue(val);

          this.val = resetVal.concat();
          this.syncValue(noCb);
        }
        this.$nextTick(() => this.setPosition(speed));
      },
      setPosition(speed = 0) {
        const transitionTime = speed === undefined ? this.speed : speed;

        this.flag || this.setTransitionTime(transitionTime);

        this.setTransform(this.position[0], this.currentSlider === 1);
        this.setTransform(this.position[1], this.currentSlider === 0);

        this.flag || this.setTransitionTime(0);
      },
      setTransform(val: number, isIdleSlider = false) {
        const slider = isIdleSlider ? this.idleSlider : this.currentSlider;
        const processSize = `${
          slider === 0 ? this.position[1] - val : val - this.position[0]
        }px`;
        const processPos = `${slider === 0 ? val : this.position[0]}px`;

        const value = this.roundToDPR(val - this.buttonsSize / 2);
        const translateValue = `translateX(${value}px)`;

        this.slider[slider].style.transform = translateValue;
        this.slider[slider].style.webkitTransform = translateValue;
        this.slider[slider].style.transform = translateValue;

        (this.$refs.sliderProcess as HTMLElement).style.width = processSize;
        (this.$refs.sliderProcess as HTMLElement).style.left = processPos;
      },
      roundToDPR(value: number) {
        const r = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
        return Math.round(value * r) / r;
      },
      setTransitionTime(time: number) {
        // In order to avoid browser merge style and modify together
        time || (this.$refs.sliderProcess as HTMLElement).offsetWidth;

        for (let i = 0; i < this.slider.length; i += 1) {
          this.slider[i].style.transitionDuration = `${time}s`;
          this.slider[i].style.webkitTransitionDuration = `${time}s`;
        }

        (this.$refs.sliderProcess as HTMLElement).style.transitionDuration = `${time}s`;
        (
          this.$refs.sliderProcess as HTMLElement
        ).style.webkitTransitionDuration = `${time}s`;
      },
      limitValue(val: number[]) {
        const inRange = (v: number) => {
          if (v < this.min) {
            this.printError(
              `The value of the slider is ${v}, the minimum value is ${this.min},
              the value of this slider can not be less than the minimum value`
            );
            return this.min;
          }
          if (v > this.max) {
            this.printError(
              `The value of the slider is ${v}, the maximum value is ${this.max},
              the value of this slider can not be greater than the maximum value`
            );
            return this.max;
          }
          return v;
        };

        return val.map((v: number) => inRange(v));
      },
      syncValue(noCb = false) {
        const val = this.val.concat();
        this.$emit('input', val);
        noCb || this.$emit('callback', val);
      },
      refresh() {
        if (this.$refs.sliderElement) {
          this.getStaticData();
          this.setPosition();
        }
      },
      printError(msg: string) {
        if (this.debug) {
          console.error(`[VueSlider error]: ${msg}`);
        }
      }
    },
    render() {
      const attributes = {
        attrs: this.$attrs,
        on: this.$listeners
      };

      return (
        <div
          class={[styles.rangeSlider, this.disabled ? styles.disabled : '']}
          {...attributes}
        >
          <p class={[styles.rangeSliderCaption, styles.rangeSliderInfo]}>
            {this.caption}
          </p>
          <p class={[styles.rangeSliderValues, styles.rangeSliderInfo]}>
            {`${this.value[0]} – ${this.value[1]}`}
          </p>
          <div
            ref="sliderElement"
            class={styles.rangeSliderWrap}
            aria-hidden="true"
            onClick={(event: MouseEvent) => this.wrapClick(event)}
          >
            <button
              ref="sliderBtnLeft"
              class={styles.rangeSliderBtn}
              style={this.dotStyles}
              onMousedown={(event: MouseEvent) => {
                this.moveSlideByClick(event, 0, false);
              }}
              onTouchstart={(event: TouchEvent) => {
                this.moveSlideByTouch(event, 0, false);
              }}
            />
            <button
              ref="sliderBtnRight"
              class={styles.rangeSliderBtn}
              style={this.dotStyles}
              onMousedown={(event: MouseEvent) => {
                this.moveSlideByClick(event, 1, false);
              }}
              onTouchstart={(event: TouchEvent) => {
                this.moveSlideByTouch(event, 1, false);
              }}
            />
            <div
              ref="sliderProcess"
              role="button"
              class={styles.rangeSliderProgress}
              onMousedown={(event: MouseEvent) => {
                this.moveSlideByClick(event, 0, true);
              }}
              onTouchstart={(event: TouchEvent) => {
                this.moveSlideByTouch(event, 0, true);
              }}
            />
          </div>
        </div>
      );
    }
  });
